import * as React from "react"
import { BookingConfirmation } from "./checkout/BookingConfirmation"
import { HireBooking } from "./checkout/HireBooking"
import { Icon } from "./checkout/components/Icon"
import { BookingFooter } from "./BookingFooter"
import { EquipmentSelector } from "./EquipmentSelector"
import { BOOKING_PRIMARY_COLOUR } from "./theme"

const apiBaseUrl = import.meta.env.VITE_BOOKING_API_BASE_URL || "http://127.0.0.1:55421/functions/v1/booking-api"
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "pk_test_replace_me"
const mainSiteUrl = import.meta.env.VITE_MAIN_SITE_URL || "https://arctic-hire.co.uk/"
const defaultProductSlug = import.meta.env.VITE_DEFAULT_PRODUCT_SLUG || ""
const supportPhone = import.meta.env.VITE_SUPPORT_PHONE || ""
const supportWhatsApp = import.meta.env.VITE_SUPPORT_WHATSAPP || ""

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/"
  const params = new URLSearchParams(window.location.search)
  const productSlug = params.get("product") || defaultProductSlug
  const hasProductLink = Boolean(params.get("type")?.trim() || params.get("product")?.trim())
  const confirmation = path === "/booking-confirmation"

  return (
    <div className="booking-app" style={{ "--booking-primary": BOOKING_PRIMARY_COLOUR } as React.CSSProperties}>
      <BookingHeader />
      <main id="booking-content" className="booking-content">
        {confirmation ? (
          <BookingConfirmation
            apiBaseUrl={apiBaseUrl}
            primaryColour={BOOKING_PRIMARY_COLOUR}
            surfaceColour="#FFFFFF"
            borderRadius={6}
            supportPhone={supportPhone}
            supportWhatsApp={supportWhatsApp}
          />
        ) : !hasProductLink ? (
          <EquipmentSelector />
        ) : (
          <HireBooking
            apiBaseUrl={apiBaseUrl}
            stripePublishableKey={stripePublishableKey}
            productSlug={productSlug}
            productSlugSource="query_only"
            ctaLabel="Check availability"
            primaryColour={BOOKING_PRIMARY_COLOUR}
            surfaceColour="#FFFFFF"
            borderRadius={6}
            supportPhone={supportPhone}
            supportWhatsApp={supportWhatsApp}
            analyticsVariant="standalone-booking"
          />
        )}
      </main>
      <BookingFooter />
    </div>
  )
}

function BookingHeader() {
  const headerRef = React.useRef<HTMLElement>(null)
  React.useLayoutEffect(() => {
    const header = headerRef.current
    if (!header) return
    const root = document.documentElement
    const update = () => root.style.setProperty("--booking-header-height", `${header.getBoundingClientRect().height}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(header)
    return () => { observer.disconnect(); root.style.removeProperty("--booking-header-height") }
  }, [])
  const phone = supportPhone.trim() || "+44 20 3411 1468"
  const phoneHref = `tel:${phone.replace(/[^+\d]/g, "")}`
  const whatsAppHref = `https://wa.me/${(supportWhatsApp.trim() || phone).replace(/\D/g, "")}`
  return (
    <header ref={headerRef} className="booking-header">
      <div className="booking-header-inner">
        <a className="brand" href={mainSiteUrl} aria-label="Arctic Hire home">
          <span className="brand-mark" aria-hidden="true" />
          <span className="brand-name">Arctic Hire</span>
        </a>
        <h1 className="booking-title"><Icon name="lock" /><span>Secure checkout</span></h1>
        <div className="booking-assistance">
          <p className="booking-assistance-label">
            <Icon name="phone" />Need help?<span className="booking-assistance-actions"> <a href={phoneHref} aria-label="Call Arctic Hire">Call</a> or <a href={whatsAppHref} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp Arctic Hire (opens in a new tab)">WhatsApp us</a></span>
          </p>
          <span className="booking-assistance-number">{phone}</span>
        </div>
      </div>
    </header>
  )
}
